import { useState, useRef, ReactElement } from 'react';
import classnames from 'classnames';

import { StaticDataFetcherItem } from '@hh.ru/magritte-common-data-provider';
import { keyboardMatches, keyboardKeys } from '@hh.ru/magritte-common-keyboard';
import { Cell } from '@hh.ru/magritte-ui-cell';
import { CheckableCardElement } from '@hh.ru/magritte-ui-checkable-card/CheckableCardElement';
import { Radio, Checkbox } from '@hh.ru/magritte-ui-checkbox-radio';
import { CheckOutlinedSize24 } from '@hh.ru/magritte-ui-icon/icon';
import { SelectOptionProps, CheckableComponentProps } from '@hh.ru/magritte-ui-select/types';
import { Text } from '@hh.ru/magritte-ui-typography';

import styles from './select-option.less';

const CheckableComponent = <DataType extends StaticDataFetcherItem>({
    type,
    value,
    checked,
    name,
    onChange,
    'data-qa': dataQa,
}: CheckableComponentProps<DataType>): ReactElement => {
    if (type === 'label') {
        return (
            <span>
                {checked && <CheckOutlinedSize24 />}
                <input type="hidden" name={name} value={value.value} />
            </span>
        );
    }
    if (type === 'radio') {
        return (
            <Radio
                data-focus-visible-skip="true"
                data-qa={dataQa}
                tabIndex={-1}
                name={name}
                value={value.value}
                checked={checked}
                onChange={(event) => {
                    onChange(value, event.target.checked);
                }}
            />
        );
    }
    return (
        <Checkbox
            data-focus-visible-skip="true"
            data-qa={dataQa}
            tabIndex={-1}
            name={name}
            value={value.value}
            checked={checked}
            onChange={(event) => {
                onChange(value, event.target.checked);
            }}
        />
    );
};

CheckableComponent.displayName = 'SelectInputRender';

export const SelectOption = <DataType extends StaticDataFetcherItem>({
    checked,
    name,
    disabled,
    render,
    type,
    onChange,
    plain,
    isMobile = true,
    data,
    multiple,
    isXS,
    'data-qa': dataQa,
}: SelectOptionProps<DataType>): ReactElement => {
    const [focusVisible, setFocusVisible] = useState(false);
    const focusedByMouseRef = useRef(false);
    const input = CheckableComponent<DataType>({ type, value: data, checked, name, onChange, 'data-qa': dataQa });
    const renderProps = {
        data,
        checked,
        multiple,
        name,
        onChange,
        input,
        type,
        plain,
        disabled,
        render,
        isXS,
    };
    if (type === 'delimiter') {
        return (
            <div className={classnames({ [styles.selectDelimiter]: !plain })}>
                <Text typography="label-3-regular">{render(renderProps)}</Text>
            </div>
        );
    }
    if (plain) {
        return (
            <div className={classnames(styles.selectOptionLabel, { [styles.selectOptionLabelDesktop]: !isMobile })}>
                <Cell
                    align="center"
                    data-qa={`magritte-select-option-${data.value}`}
                    data-magritte-select-option={data.value}
                    right={type === 'label' ? input : undefined}
                    vertPadding={isMobile}
                    disabled={disabled}
                    onClick={() => {
                        onChange(data, !checked);
                    }}
                >
                    {render(renderProps)}
                </Cell>
            </div>
        );
    }
    return (
        <CheckableCardElement
            Element="label"
            padding={16}
            borderRadius={12}
            flexible={false}
            checked={checked}
            data-magritte-select-option={data.value}
            focusVisible={focusVisible}
            data-qa={`magritte-select-option-${data.value}`}
            disabled={disabled}
            toneHover
            onClick={() => {
                if (!plain && type === 'label') {
                    onChange(data, !checked);
                }
            }}
            onMouseDown={() => {
                focusedByMouseRef.current = true;
            }}
            onKeyDown={(event) => {
                if (keyboardMatches(event.nativeEvent, [keyboardKeys.Enter, keyboardKeys.Space])) {
                    onChange(data, !checked);
                    event.preventDefault();
                    setFocusVisible(true);
                }
            }}
            onFocus={(event) => {
                if (!event.target.dataset.focusVisibleSkip) {
                    return;
                }
                if (focusedByMouseRef.current) {
                    focusedByMouseRef.current = false;
                    return;
                }
                setFocusVisible(true);
            }}
            onBlur={() => {
                if (focusVisible) {
                    setFocusVisible(false);
                }
            }}
        >
            {render(renderProps)}
        </CheckableCardElement>
    );
};
